import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  Copy,
  Trash2,
  MessageSquare,
  Loader2,
  AlertCircle,
  Filter
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useTemplates, useCreateTemplate, useUpdateTemplate, useDeleteTemplate, useDuplicateTemplate } from "@/hooks/api-hooks";
import { useAuth } from "@/contexts/auth-context";

// Real modal components
import { UnifiedTemplateModal } from "@/components/modals/UnifiedTemplateModal";
import { TemplateNameModal } from "@/components/modals/TemplateNameModal";

export function TemplatesPage() {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTypes, setSelectedTypes] = useState<string[]>(["feedback", "one_on_one"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // Modal states
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'preview'>('create');
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateToDelete, setTemplateToDelete] = useState<{id: string, name: string} | null>(null);

  // Fetch all templates (both feedback and one_on_one)
  const {
    data: feedbackTemplatesResponse,
    isLoading: isLoadingFeedback,
    error: feedbackError
  } = useTemplates({ type: 'feedback', includeDefaults: true });

  const {
    data: oneOnOneTemplatesResponse,
    isLoading: isLoadingOneOnOne,
    error: oneOnOneError
  } = useTemplates({ type: 'one_on_one', includeDefaults: true });

  const isLoading = isLoadingFeedback || isLoadingOneOnOne;
  const error = feedbackError || oneOnOneError;

  // Combine both template types
  const allTemplates = useMemo(() => {
    const feedbackTemplates = feedbackTemplatesResponse?.data || [];
    const oneOnOneTemplates = oneOnOneTemplatesResponse?.data || [];
    return [...feedbackTemplates, ...oneOnOneTemplates];
  }, [feedbackTemplatesResponse, oneOnOneTemplatesResponse]);

  // Filter templates based on category, type, and search
  const filteredTemplates = useMemo(() => {
    return allTemplates.filter(template => {
      const matchesCategory = selectedCategory === "all" || template.category === selectedCategory;
      const matchesType = selectedTypes.includes(template.templateType);
      const normalizedSearch = searchQuery.toLowerCase();
      const nameValue = template.name?.toLowerCase() ?? "";
      const descriptionValue = template.description?.toLowerCase() ?? "";
      const categoryValue = template.category?.toLowerCase() ?? "";
      const matchesSearch = searchQuery === "" ||
        nameValue.includes(normalizedSearch) ||
        descriptionValue.includes(normalizedSearch) ||
        categoryValue.includes(normalizedSearch);
      return matchesCategory && matchesType && matchesSearch;
    });
  }, [allTemplates, selectedCategory, selectedTypes, searchQuery]);

  const totalPages = useMemo(() => {
    if (filteredTemplates.length === 0) return 1;
    return Math.ceil(filteredTemplates.length / PAGE_SIZE);
  }, [filteredTemplates.length]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedTypes, searchQuery]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedTemplates = useMemo(() => {
    if (filteredTemplates.length === 0) return [];
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredTemplates.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredTemplates, currentPage]);

  // Get unique categories from templates
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(allTemplates.map(t => t.category))];
    return ["all", ...uniqueCategories];
  }, [allTemplates]);

  const handleTypeToggle = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const formatTemplateType = (type: string) => {
    switch (type) {
      case 'feedback':
        return 'Feedback';
      case 'one_on_one':
        return '1-on-1';
      default:
        return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'feedback':
        return 'bg-surface-variant text-foreground';
      case 'one_on_one':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // API hooks
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();
  const duplicateTemplate = useDuplicateTemplate();
  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setModalMode('create');
    setIsTemplateModalOpen(true);
  };

  const handlePreviewTemplate = (templateId: string) => {
    const template = filteredTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setModalMode('preview');
      setIsTemplateModalOpen(true);
    }
  };

  const handleEditTemplate = (templateId: string) => {
    const template = filteredTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setModalMode('edit');
      setIsTemplateModalOpen(true);
    }
  };

  const handleDuplicateTemplate = (templateId: string) => {
    const template = filteredTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setIsDuplicateModalOpen(true);
    }
  };

  const handleDeleteTemplate = (templateId: string, templateName: string) => {
    setTemplateToDelete({ id: templateId, name: templateName });
    setIsDeleteDialogOpen(true);
  };

  const handleSaveTemplate = async (templateData: any) => {
    try {
      if (selectedTemplate) {
        // Update existing template
        await updateTemplate.mutateAsync({
          id: selectedTemplate.id,
          ...templateData
        });
      } else {
        // Create new template
        await createTemplate.mutateAsync(templateData);
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      throw error;
    }
  };

  const handleDuplicateConfirm = async (name: string, description?: string) => {
    if (!selectedTemplate) return;

    try {
      await duplicateTemplate.mutateAsync({
        id: selectedTemplate.id,
        name,
        description: description || selectedTemplate.description
      });
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      throw error;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      await deleteTemplate.mutateAsync(templateToDelete.id);
      setIsDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const closeAllModals = () => {
    setIsTemplateModalOpen(false);
    setIsDuplicateModalOpen(false);
    setSelectedTemplate(null);
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Templates</h1>
              <p className="text-muted-foreground">
                Manage feedback and 1-on-1 templates for your organization
              </p>
            </div>
            <Button onClick={handleCreateTemplate} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Template
            </Button>
          </div>

      <Card>
        <CardHeader>
          <CardTitle>All Templates</CardTitle>
          <CardDescription>
            Templates for feedback cycles and 1-on-1 meetings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4 py-4">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Type Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="mr-2 h-4 w-4" />
                  Type ({selectedTypes.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuCheckboxItem
                  checked={selectedTypes.includes("feedback")}
                  onCheckedChange={() => handleTypeToggle("feedback")}
                >
                  Feedback Templates
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={selectedTypes.includes("one_on_one")}
                  onCheckedChange={() => handleTypeToggle("one_on_one")}
                >
                  1-on-1 Templates
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category === "all" ? "All Categories" : category}
                </option>
              ))}
            </select>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading templates...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <span className="ml-2 text-destructive">Failed to load templates</span>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-muted-foreground py-8">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Search className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No templates found</h3>
              <p className="text-sm mb-4">
                {selectedCategory !== "all" || selectedTypes.length < 2
                  ? "Try adjusting your filters or search"
                  : "Create your first template to get started"}
              </p>
              {(selectedCategory === "all" && selectedTypes.length === 2) && (
                <Button onClick={handleCreateTemplate} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Template
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-muted/50 rounded-lg text-sm font-medium text-muted-foreground">
                <div className="col-span-4">Template Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-1">Questions</div>
                <div className="col-span-1">Usage</div>
                <div className="col-span-1">Created By</div>
                <div className="col-span-1">Actions</div>
              </div>

              {/* Table Body */}
              {paginatedTemplates.map((template: any) => (
                <div
                  key={template.id}
                  className="grid grid-cols-12 gap-4 px-4 py-3 border border-border rounded-lg hover:border-border/80 transition-colors"
                >
                  <div className="col-span-4 flex items-center space-x-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{template.name}</div>
                      {template.isDefault && (
                        <Badge variant="secondary" className="text-xs mt-1">
                          Default
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="col-span-2 flex items-center">
                    <Badge className={`text-xs ${getTypeColor(template.templateType)}`}>
                      {formatTemplateType(template.templateType)}
                    </Badge>
                  </div>

                  <div className="col-span-2 flex items-center">
                    <Badge variant="outline" className="capitalize">
                      {template.category}
                    </Badge>
                  </div>

                  <div className="col-span-1 flex items-center">
                    <div className="flex items-center space-x-1">
                      <MessageSquare className="h-3 w-3 text-muted-foreground" />
                      <span>{template.questionCount || 0}</span>
                    </div>
                  </div>

                  <div className="col-span-1 flex items-center">
                    <span className="text-sm text-foreground-secondary">
                      {template.usageCount || 0} times
                    </span>
                  </div>

                  <div className="col-span-1 flex items-center">
                    <span className="text-sm">
                      {template.creator ?
                        `${template.creator.firstName} ${template.creator.lastName}` :
                        'System'
                      }
                    </span>
                  </div>

                  <div className="col-span-1 flex items-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handlePreviewTemplate(template.id)}>
                          <Eye className="mr-2 h-4 w-4" />
                          Preview
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditTemplate(template.id)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicateTemplate(template.id)}>
                          <Copy className="mr-2 h-4 w-4" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDeleteTemplate(template.id, template.name)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-4 py-3 border-t border-border mt-4">
              <div className="text-sm text-muted-foreground">
                {filteredTemplates.length === 0 ? (
                  'No templates to display'
                ) : (
                  (() => {
                    const startIndex = (currentPage - 1) * PAGE_SIZE + 1;
                    const endIndex = Math.min(currentPage * PAGE_SIZE, filteredTemplates.length);
                    return `Showing ${startIndex}–${endIndex} of ${filteredTemplates.length} templates`;
                  })()
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {Math.min(currentPage, totalPages)} of {totalPages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages || filteredTemplates.length === 0}
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Templates</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{allTemplates.length}</div>
            <p className="text-xs text-muted-foreground">
              Across all types
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Feedback Templates</CardTitle>
            <Badge className="bg-surface-variant text-foreground text-xs">Feedback</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {allTemplates.filter(t => t.templateType === 'feedback').length}
            </div>
            <p className="text-xs text-muted-foreground">
              For feedback cycles
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">1-on-1 Templates</CardTitle>
            <Badge className="bg-green-100 text-green-800 text-xs">1-on-1</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {allTemplates.filter(t => t.templateType === 'one_on_one').length}
            </div>
            <p className="text-xs text-muted-foreground">
              For 1-on-1 meetings
            </p>
          </CardContent>
        </Card>
      </div>
      </div>
      </div>

      {/* Unified Modal */}
      <UnifiedTemplateModal
        isOpen={isTemplateModalOpen}
        onClose={closeAllModals}
        onSave={handleSaveTemplate}
        existingTemplate={selectedTemplate}
        mode={modalMode}
      />

      <TemplateNameModal
        isOpen={isDuplicateModalOpen}
        onClose={() => {
          setIsDuplicateModalOpen(false);
          setSelectedTemplate(null);
        }}
        onConfirm={handleDuplicateConfirm}
        title="Duplicate Template"
        subtitle="Enter a name for the duplicated template"
        existingName={selectedTemplate?.name}
        existingDescription={selectedTemplate?.description}
        action="Duplicate"
      />

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <p className="text-muted-foreground mt-2">
              Are you sure you want to delete the template "{templateToDelete?.name}"? This action cannot be undone.
            </p>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setTemplateToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
